<template>
  <div class="modal-overlay" @click.self="handleClose">
    <div class="modal-container">
      <div class="modal-header">
        <h3>Secure Aadhaar e-Sign</h3>
        <button @click="handleClose" class="close-btn" title="Close">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <iframe
          :src="signingLink"
          class="signing-iframe"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          title="Document Signing"
        ></iframe>
      </div>

      <div class="modal-footer">
        <p class="footer-note">
          <i class="pi pi-shield"></i>
          This is a secure window. Do not close until signing is complete.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  signingLink: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['close', 'completed']);

function receiveMessage(event) {
  // Digio sends postMessage events on completion
  console.log('Received postMessage from Digio:', event.data);

  // Handle both string and object responses
  let eventData = event.data;

  // If it's a string, check for success keywords
  if (typeof eventData === 'string') {
    if (eventData.includes('DIGIO_SIGN_SUCCESS') ||
        eventData.includes('success') ||
        eventData.includes('completed')) {
      console.log('Document signing completed successfully (string)');
      emit('completed');
      return;
    }
  }

  // If it's an object, check the message property
  if (typeof eventData === 'object' && eventData !== null) {
    const message = eventData.message || '';

    // Check for success keywords in the message
    if (message.includes('approved successfully') ||
        message.includes('success') ||
        message.includes('completed') ||
        message.includes('signed successfully')) {
      console.log('Document signing completed successfully (object):', message);
      emit('completed');
      return;
    }
  }
}

function handleClose() {
  const confirmed = window.confirm('Are you sure you want to close? Make sure you have completed the signing process.');
  if (confirmed) {
    emit('close');
  }
}

onMounted(() => {
  window.addEventListener('message', receiveMessage);
  console.log('Digio sign modal mounted, listening for postMessage events');
});

onBeforeUnmount(() => {
  window.removeEventListener('message', receiveMessage);
  console.log('Digio sign modal unmounted, removed postMessage listener');
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #e9ecef;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modal-header h3 {
  margin: 0;
  color: var(--an-text-primary);
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
}

.close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: white;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  font-size: 18px;
}

.close-btn:hover {
  background: #dc3545;
  color: white;
  transform: rotate(90deg);
}

.modal-body {
  flex: 1;
  overflow: hidden;
  background: white;
}

.signing-iframe {
  width: 100%;
  height: 100%;
  min-height: 500px;
  border: none;
  display: block;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 2px solid #e9ecef;
  background: #f8f9fa;
}

.footer-note {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
}

.footer-note i {
  color: #28a745;
  font-size: 18px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-container {
    max-height: 95vh;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-header h3 {
    font-size: 1.2rem;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .signing-iframe {
    min-height: 400px;
  }

  .modal-footer {
    padding: 12px 20px;
  }

  .footer-note {
    font-size: 0.8rem;
  }
}
</style>
